import { Alert, Form, Input, Select } from '@vivaldis/antd-ui'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider } from '../../../../../Divider'
import { useAiSuggestion } from '../../../../providers/AiSuggestionProvider'
import { useForm } from '../../../../providers/FormProvider'
import { PositionWithWPS } from '../../../../types'
import { wpsToWPSFormValues } from '../../../../utils/wpsToWPSFormValues'
import { LabelWithAISuggestion } from '../../../LabelWithAISuggestion'

interface Props {
  positionsWithWPS?: PositionWithWPS[]
}

export const Step1 = ({ positionsWithWPS }: Props) => {
  const [t] = useTranslation('web_ui')

  const { setValues, resetForm } = useForm()
  const { aiSuggestedWorkPostSheet, isFieldAiSuggested } = useAiSuggestion()

  const positionsWithWPSOptions = useMemo(
    () =>
      (positionsWithWPS || []).map(positionsWithWPS => ({
        label: positionsWithWPS.name,
        value: positionsWithWPS.id
      })),
    [positionsWithWPS]
  )

  const showCopyFromPositionButton = positionsWithWPSOptions.length > 0

  const handlePositionWithWPSChange = useCallback(
    (positionId: unknown | undefined) => {
      if (!positionId) {
        return resetForm()
      }

      const positionWithWPS = positionsWithWPS?.find(
        position => position.id === positionId
      )

      if (positionWithWPS) {
        setValues(prevValues => {
          return {
            ...prevValues,
            copyFromPositionId: positionId,
            ...wpsToWPSFormValues(positionWithWPS.workPostSheet)
          }
        })
      }
    },
    [positionsWithWPS, resetForm, setValues]
  )

  return (
    <>
      {aiSuggestedWorkPostSheet ? (
        <Alert
          type="info"
          title={t('work_post_sheet_form.ai_prefilled_alert')}
          closable
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {showCopyFromPositionButton ? (
        <Form.Item
          name="copyFromPositionId"
          label={t('work_post_sheet_form.copyFromPositionId.label')}
        >
          <Select
            name="copyFromPositionId"
            placeholder={t('form.optional')}
            options={positionsWithWPSOptions}
            onChange={handlePositionWithWPSChange}
            showSearch
            allowClear
          />
        </Form.Item>
      ) : null}

      <Form.Item
        name="tasksToPerform"
        required
        label={
          <LabelWithAISuggestion
            label={t('work_post_sheet_form.tasksToPerform.label')}
            aiSuggested={isFieldAiSuggested('tasksToPerform')}
          />
        }
        tooltip={t('work_post_sheet_form.tasksToPerform.hint')}
      >
        <Input.TextArea
          name="tasksToPerform"
          placeholder={t('form.required')}
          autoSize
        />
      </Form.Item>
      <Form.Item
        name="requiredQualifications"
        required
        label={
          <LabelWithAISuggestion
            label={t('work_post_sheet_form.requiredQualifications.label')}
            aiSuggested={isFieldAiSuggested('requiredQualifications')}
          />
        }
        tooltip={t('work_post_sheet_form.requiredQualifications.hint')}
      >
        <Input.TextArea
          name="requiredQualifications"
          placeholder={t('form.required')}
          autoSize
        />
      </Form.Item>
      <Form.Item
        name="workEquipment"
        required
        label={
          <LabelWithAISuggestion
            label={t('work_post_sheet_form.workEquipment.label')}
            aiSuggested={isFieldAiSuggested('workEquipment')}
          />
        }
        tooltip={t('work_post_sheet_form.workEquipment.hint')}
      >
        <Input.TextArea
          name="workEquipment"
          placeholder={t('form.required')}
          autoSize
        />
      </Form.Item>
      <Form.Item
        name="priorInstructions"
        required
        label={
          <LabelWithAISuggestion
            label={t('work_post_sheet_form.priorInstructions.label')}
            aiSuggested={isFieldAiSuggested('priorInstructions')}
          />
        }
        tooltip={t('work_post_sheet_form.priorInstructions.hint')}
      >
        <Input.TextArea
          name="priorInstructions"
          placeholder={t('form.required')}
          autoSize
        />
      </Form.Item>
      <Form.Item
        name="foreseeableTraining"
        required
        label={
          <LabelWithAISuggestion
            label={t('work_post_sheet_form.foreseeableTraining.label')}
            aiSuggested={isFieldAiSuggested('foreseeableTraining')}
          />
        }
        tooltip={t('work_post_sheet_form.foreseeableTraining.hint')}
      >
        <Input.TextArea
          name="foreseeableTraining"
          placeholder={t('form.required')}
          autoSize
        />
      </Form.Item>
      <Form.Item
        name="workstationLocation"
        required
        label={
          <LabelWithAISuggestion
            label={t('work_post_sheet_form.workstationLocation.label')}
            aiSuggested={isFieldAiSuggested('workstationLocation')}
          />
        }
        tooltip={t('work_post_sheet_form.workstationLocation.hint')}
      >
        <Input.TextArea
          name="workstationLocation"
          placeholder={t('form.required')}
          autoSize
        />
      </Form.Item>

      <Divider sectionSeparator />

      <Form.Item
        name="welcomeOfficer"
        required
        label={t('work_post_sheet_form.welcomeOfficer.label')}
        tooltip={t('work_post_sheet_form.welcomeOfficer.hint')}
      >
        <Input name="welcomeOfficer" placeholder={t('form.required')} />
      </Form.Item>
      <Form.Item
        name="welcomeOfficerPosition"
        required
        label={t('work_post_sheet_form.welcomeOfficerPosition.label')}
        tooltip={t('work_post_sheet_form.welcomeOfficerPosition.hint')}
      >
        <Input name="welcomeOfficerPosition" placeholder={t('form.required')} />
      </Form.Item>
    </>
  )
}
